<script setup>
import { ref, computed } from 'vue';
import i18n from '@/plugins/i18n';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import Button from 'primevue/button';
import OverlayPanel from 'primevue/overlaypanel';
import { RouteNames } from '@/data/routes';
import analytics from '@/utils/analytics';
import { SEGMENT_EVENTS } from '@/data/segment-events';
import { getShortBillId } from '@/utils/payments';
import CustomLink from '@/components/CustomLink.vue';

const store = useStore();
const router = useRouter();

const notificationPanel = ref(null);

function togglePopover(event) {
  notificationPanel.value.toggle(event);
}

function handleTaskClick(callback) {
  callback?.();
  notificationPanel.value.hide();
}

const pendingContracts = computed(() => store.getters.pendingContracts ?? []);

function handlePendingContract() {
  const firstPendingContract = pendingContracts.value[0];
  if (firstPendingContract) {
    analytics.track(SEGMENT_EVENTS.if_contract_task_clicked, {
      businessId: store.getters.business.id,
      businessName: store.getters.business.name,
      shortBillId: getShortBillId(firstPendingContract.billId),
      billId: firstPendingContract.billId,
      signatory: store.getters.isAuthorizedSignatory,
    });
    router.push({
      name: RouteNames.HOME.INITIAL,
      params: { billId: firstPendingContract.billId },
    });
  }
}

const authorizedSignatoryFullName = computed(() => {
  const { authorizedSignatory } = store.getters;
  if (authorizedSignatory) {
    return store.getters.authorizedSignatory.fullName;
  }
  return undefined;
});

const pendingItems = computed(() => {
  const { length } = pendingContracts.value;
  return [
    {
      text: [
        i18n.t('paymentsPage.pendingContracts.awaitingSignature'),
        i18n.t('paymentsPage.pendingContracts.completeSigning'),
      ],
      linkText: i18n.t('paymentsPage.pendingContracts.linkText'),
      severity: 'warn',
      onClick: handlePendingContract,
      show: length && store.getters.isAuthorizedSignatory,
    },
    {
      text: [
        i18n.t('paymentsPage.pendingContracts.awaitingSignature', {
          fullName: authorizedSignatoryFullName.value,
          length,
          contract: i18n.tc('paymentsPage.pendingContracts.contract', length),
        }),
        i18n.t('paymentsPage.pendingContracts.viewDetails'),
      ],
      severity: 'warn',
      onClick: handlePendingContract,
      linkText: i18n.t('paymentsPage.pendingContracts.linkText'),
      show: length && !store.getters.isAuthorizedSignatory,
    },
  ].filter((item) => item.show);
});

const severityIconMap = {
  warn: 'pi-exclamation-triangle',
  info: 'pi-info-circle',
  success: 'pi-check',
};
</script>

<template>
  <div v-if="pendingItems.length" :class="$style['notification-container']">
    <Button
      icon="pi pi-bell"
      variant="outlined"
      severity="warn"
      :class="$style['alert-button']"
      aria-label="Notification"
      @click="togglePopover"
    />

    <OverlayPanel
      id="popover"
      ref="notificationPanel"
      :class="$style['tasks-container']"
      :style="{
        padding: 0,
      }"
    >
      <div>
        <span class="font-medium block mb-2">Pending Tasks </span>
        <div :class="$style['tasks-list']">
          <ul>
            <li
              v-for="(item, index) in pendingItems"
              :key="index"
              :class="[$style['task-item'], $style[item.severity]]"
              @click="handleTaskClick(item.onClick)"
            >
              <i :class="`pi ${severityIconMap[item.severity]}`" />
              <div>
                <span
                  v-for="(iText, iIndex) in item.text"
                  :key="iIndex"
                  :class="$style['task-item-text']"
                >
                  <CustomLink
                    :text="iText"
                    :link-text="item.linkText"
                    @click="handleTaskClick(item.onClick)"
                  />
                </span>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </OverlayPanel>
  </div>
</template>

<style module>
.notification-container {
  top: 10px;
  right: 10px;
  padding: 0 7px 4px;

  .alert-button {
    height: 2.5rem;
    border-radius: 50%;
    background-color: transparent;
    border: none;
    color: black;
  }

  .alert-button:enabled:hover,
  .alert-button:enabled:focus {
    color: black;
    background-color: #f0f0f0;
    box-shadow: none;
  }
}

.tasks-container {
  background: white;
  border-radius: 8px;
  padding: 16px;
  width: 560px;
  box-shadow: 0 2px 10px rgb(0 0 0 / 10%);
  text-align: left;

  @media only screen and (max-width: 600px) {
    width: 100%;
  }
}

.tasks-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.task-item {
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 6px;
  margin-bottom: 6px;
  font-size: 14px;
  transition: background 0.3s ease-in-out;

  i {
    padding: 0 2px;

    @media only screen and (max-width: 400px) {
      display: none;
    }
  }

  .task-item-text {
    display: block;

    a {
      color: #297fe3;
    }
  }
}

.warn {
  background: #ffecbc;
}

.warn:hover {
  background: #ffe5a3;
}

.info {
  background: #dee9f599;
}

.info:hover {
  background: #dee9f5;
}

.success {
  background: #def2e799;
}

.success:hover {
  background: #def2e7;
}
</style>
